// TexturePresetPicker — テクスチャ preset の共有ピッカー (Modern / Classic 両用).
//
// 背景 (modern-layout.md §1.1 ⭐️確定 2026-06-13「キャンバスに乗る表現は両レイアウト
// 同時」): テクスチャは当初 Modern のみ露出だったが §1.1 で表現パリティが確定し、
// Classic でも露出する。Modern (`ModernToolProperties`) が持っていた preset 選択 UI
// (TEXTURE_PRESET_OPTIONS + サムネ描画 + 選択ハイライト) を **重複実装を避けて** ここに
// 切り出し、Modern と Classic の双方から使う。
//
// 本コンポーネントは「4 種サムネのグリッド + 選択ハイライト + クリックで
// `onSelectTexturePreset(id)`」だけを担う純粋な部品 (= レイアウト非依存)。
// - Modern: ペン種 chip が「テクスチャ」のとき `<TexturePresetPicker>` を inline 表示。
// - Classic: ToolPanel の「テクスチャ」入口ボタンが開く floating panel 内に表示。
//
// pen='texture' への切替や penParams への反映は呼び出し側 (= 共有 chrome の
// `onSelectPen('texture')` / `onSelectTexturePreset`、penParamsFor) の責務で、本部品は
// 「どの preset を選ぶか」だけを表現する (= engine / protocol 不変)。
//
// preset 一覧 (texture-brush-design.md §1 / §10.1 / §12):
//   紙 → 毛先 → 雲 → カケアミ の 4 種のみ。
//   - `'spray'` は除外 (consult 2026-06-19): 動的散布は別ペン `airbrush` の守備範囲。
//   - 網点 (screentone) は `pen='tone'` カテゴリへ昇格済 (§12.2) なので texture preset
//     には含めない。
//   preset 追加時はこの配列を伸ばすだけ。サムネは samplerForPreset(preset) の raw
//   alpha を grayscale で描く (= 柄が一目で分かる、追加配線不要)。

import { samplerForPreset } from '@oekaki/drawing-core';
import type { TexturePreset } from '@oekaki/protocol';
import { useEffect, useRef } from 'react';

/**
 * テクスチャ preset の選択肢。`id` は wire の `TexturePreset`、`label` は日本語表示名。
 * Modern / Classic 共通 (= 表現パリティ、両レイアウトで同じ 4 種を出す)。
 */
export const TEXTURE_PRESET_OPTIONS: ReadonlyArray<{
  readonly id: TexturePreset;
  readonly label: string;
}> = [
  { id: 'paper', label: '紙' },
  { id: 'bristle', label: '毛先' },
  { id: 'cloud', label: '雲' },
  { id: 'hatch', label: 'カケアミ' },
];

export interface TexturePresetPickerProps {
  /** 現在選択中の preset (= ハイライト対象)。 */
  readonly value: TexturePreset;
  /** preset サムネクリックで発火。呼び出し側が pen.texturePreset を書く。 */
  readonly onSelectTexturePreset: (preset: TexturePreset) => void;
}

/**
 * 4 種 preset サムネのグリッド。選択中を accent でハイライトする。レイアウトに
 * 依らないよう wrapper の余白等は最小に保ち、親 (Modern fieldset / Classic floating
 * panel) 側で配置する。
 */
export const TexturePresetPicker = ({
  value,
  onSelectTexturePreset,
}: TexturePresetPickerProps): React.ReactNode => (
  <div className="flex flex-wrap gap-1">
    {TEXTURE_PRESET_OPTIONS.map((opt) => {
      const active = value === opt.id;
      return (
        <button
          key={opt.id}
          type="button"
          aria-pressed={active}
          aria-label={`テクスチャ ${opt.label}`}
          title={opt.label}
          onClick={() => onSelectTexturePreset(opt.id)}
          className={`flex flex-col items-center gap-0.5 rounded border p-1 transition-colors ${
            active
              ? 'border-[var(--color-accent)] bg-[var(--color-accent)]'
              : 'border-[var(--color-border)] bg-[var(--color-bg-soft)] hover:bg-[var(--color-bg)]'
          }`}
        >
          <TexturePresetThumb preset={opt.id} />
          <span
            className={`select-none text-[9px] font-bold leading-none ${
              active ? 'text-black' : 'text-white'
            }`}
          >
            {opt.label}
          </span>
        </button>
      );
    })}
  </div>
);

/**
 * テクスチャ preset のサムネイル (texture-brush-design.md §10.1)。`samplerForPreset`
 * の raw alpha (0..255 の per-texel multiplier、drawing-core 同梱 = サーバ snapshot と
 * 同一アセット) を grayscale で小 canvas に描き、preset の柄を一目で見せる。
 *
 * - 反転して描く: alpha 大 (= 濃く塗る texel) を **黒** に、alpha 0 (= 塗らない) を
 *   **白** にマップ (= 白地に黒インクの見た目 = キャンバスでの描き味と直感が一致)。
 * - texture が thumb より大/小どちらでも tiling せず「texture 全体を thumb に収める」
 *   (= 柄の全体像を見せる)。
 * - 純粋に preset (= 不変アセット) だけに依存するので preset 変化時のみ再描画。
 */
const THUMB_PX = 28 as const;

const TexturePresetThumb = ({ preset }: { readonly preset: TexturePreset }): React.ReactNode => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const tex = samplerForPreset(preset);
    const out = new Uint8ClampedArray(THUMB_PX * THUMB_PX * 4);
    for (let y = 0; y < THUMB_PX; y++) {
      const ty = Math.min(tex.height - 1, Math.floor((y * tex.height) / THUMB_PX));
      for (let x = 0; x < THUMB_PX; x++) {
        const tx = Math.min(tex.width - 1, Math.floor((x * tex.width) / THUMB_PX));
        const a = tex.alpha[ty * tex.width + tx] ?? 0;
        // alpha 大 = 濃い塗り → 黒、alpha 0 = 塗らない → 白 (= 白地に黒インク)。
        const v = 255 - a;
        const o = (y * THUMB_PX + x) * 4;
        out[o] = v;
        out[o + 1] = v;
        out[o + 2] = v;
        out[o + 3] = 255;
      }
    }
    ctx.putImageData(new ImageData(out, THUMB_PX, THUMB_PX), 0, 0);
  }, [preset]);

  return (
    <canvas
      ref={canvasRef}
      width={THUMB_PX}
      height={THUMB_PX}
      // 装飾的 (= 親 <button> の aria-label が preset 名を読み上げる)。role も
      // aria-hidden も付けない (canvas は focusable 扱いで aria-hidden と衝突する、
      // biome a11y)。空の accessible name のままで支援技術には実質無視される。
      className="block rounded-sm"
      style={{ width: THUMB_PX, height: THUMB_PX, imageRendering: 'pixelated' }}
    />
  );
};
